import React, { useState, useEffect } from "react";
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  IconButton, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Paper,
  Divider,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GavelIcon from "@mui/icons-material/Gavel";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FilterListIcon from "@mui/icons-material/FilterList";
import DescriptionIcon from "@mui/icons-material/Description";
import { useSelector } from "react-redux";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import { convertMessageToHtml } from "../../utilities/helpers";
import { MESSAGE_STYLES } from "../../utilities/constants";

const WarningLetterSummary = () => {
  // Tab State
  const [activeTab, setActiveTab] = useState(0);

  // State for warning letters
  const [warningLetters, setWarningLetters] = useState([]);
  const [warningLetterPage, setWarningLetterPage] = useState(1);
  const [warningLetterTotalPages, setWarningLetterTotalPages] = useState(1);
  const [warningLetterTotalCount, setWarningLetterTotalCount] = useState(0);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [isLoadingWarningLetters, setIsLoadingWarningLetters] = useState(false);
  
  // State for all warning letters (unfiltered) for client-side filtering
  const [allWarningLettersList, setAllWarningLettersList] = useState([]);
  
  // Filter state
  const [countryFilter, setCountryFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [filteredWarningLetters, setFilteredWarningLetters] = useState([]);
  const [filteredPage, setFilteredPage] = useState(1);
  const [filteredTotalPages, setFilteredTotalPages] = useState(1);
  const [filteredTotalCount, setFilteredTotalCount] = useState(0);
  const [isLoadingFilteredList, setIsLoadingFilteredList] = useState(false);
  
  // State for selected company/warning letter
  const [selectedCompanyData, setSelectedCompanyData] = useState(null);
  const [isLoadingCompanyDetails, setIsLoadingCompanyDetails] = useState(false);
  
  // UI State
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedWarningLetter, setSelectedWarningLetter] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCompanyPicker, setOpenCompanyPicker] = useState(false);

  const idToken = useSelector((state) => state.token);

  const requestHeader = {
    headers: {
      Authorization: idToken,
    },
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchWarningLetters(warningLetterPage === 1 && !companySearchTerm);
    }
    // eslint-disable-next-line
  }, [warningLetterPage, companySearchTerm, activeTab]);

  // Fetch all warning letters for filtering when Tab 1 is active
  useEffect(() => {
    if (activeTab === 1 && allWarningLettersList.length === 0) {
      fetchAllWarningLettersForFilter();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  // Apply client-side filters when filters or page change
  useEffect(() => {
    if (activeTab === 1 && allWarningLettersList.length > 0) {
      applyFilters();
    }
    // eslint-disable-next-line
  }, [countryFilter, yearFilter, filteredPage, allWarningLettersList]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Fetch all warning letters for client-side filtering
  const fetchAllWarningLettersForFilter = async () => {
    try {
      setIsLoadingFilteredList(true);
      setErrorMessage(null);
      
      // Fetch all warning letters for client-side filtering
      const response = await ieqipCoreServiceApi.get(
        `/api/warning-letter/extractions?page=1&page_size=100`, 
        requestHeader
      );
      
      const data = response.data;
      
      // Handle both array and paginated response formats
      let warningLetterItems = [];
      if (Array.isArray(data)) {
        warningLetterItems = data;
      } else {
        warningLetterItems = data.warning_letters || data.items || data || [];
      }
      
      setAllWarningLettersList(warningLetterItems);
      
      // Extract unique countries and years for filters
      const countries = [...new Set(warningLetterItems.map(wl => wl.company_country).filter(c => c))].sort();
      const years = [...new Set(warningLetterItems.map(wl => {
        if (wl.date_issued) {
          return new Date(wl.date_issued).getFullYear().toString();
        }
        return null;
      }).filter(y => y))].sort().reverse();
      
      setAvailableCountries(countries);
      setAvailableYears(years);
      
      // Apply initial filters
      applyFiltersToData(warningLetterItems);
      
    } catch (error) {
      console.error("Failed to fetch warning letters for filter:", error);
      setErrorMessage(`Failed to fetch warning letters: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoadingFilteredList(false);
    }
  };

  // Apply filters to the data
  const applyFilters = () => {
    applyFiltersToData(allWarningLettersList);
  };

  const applyFiltersToData = (data) => {
    let filtered = [...data];
    
    // Apply country filter
    if (countryFilter) {
      filtered = filtered.filter(wl => wl.company_country === countryFilter);
    }
    
    // Apply year filter
    if (yearFilter) {
      filtered = filtered.filter(wl => {
        if (wl.date_issued) {
          return new Date(wl.date_issued).getFullYear().toString() === yearFilter;
        }
        return false;
      });
    }
    
    // Calculate pagination
    const pageSize = 20;
    const totalCount = filtered.length;
    const totalPages = Math.ceil(totalCount / pageSize) || 1;
    const startIndex = (filteredPage - 1) * pageSize;
    const paginatedData = filtered.slice(startIndex, startIndex + pageSize);
    
    setFilteredWarningLetters(paginatedData);
    setFilteredTotalPages(totalPages);
    setFilteredTotalCount(totalCount);
  };

  const handleCountryFilterChange = (event) => {
    setCountryFilter(event.target.value);
    setFilteredPage(1);
  };

  const handleYearFilterChange = (event) => {
    setYearFilter(event.target.value);
    setFilteredPage(1);
  };

  const handleClearFilters = () => {
    setCountryFilter("");
    setYearFilter("");
    setFilteredPage(1);
  };

  const handleFilteredPageChange = (event, value) => {
    setFilteredPage(value);
  };

  const handleRefreshFilteredList = () => {
    setAllWarningLettersList([]);
    setAvailableCountries([]);
    setAvailableYears([]);
    fetchAllWarningLettersForFilter();
  };

  // Fetch warning letters from paginated endpoint
  const fetchWarningLetters = async (autoSelectFirst = false) => {
    try {
      setIsLoadingWarningLetters(true);
      setErrorMessage(null);
      
      const params = new URLSearchParams({
        page: warningLetterPage.toString(),
        page_size: '20'
      });
      
      if (companySearchTerm) {
        params.append('company_name', companySearchTerm);
      }
      
      const response = await ieqipCoreServiceApi.get(
        `/api/warning-letter/extractions?${params.toString()}`, 
        requestHeader
      );
      
      const data = response.data;
      
      setWarningLetters(data.warning_letters || []);
      setWarningLetterTotalPages(data.total_pages || 1);
      setWarningLetterTotalCount(data.total_count || 0);
      
      // Auto-select first company only on initial load
      if (autoSelectFirst && !selectedCompanyData && data.warning_letters && data.warning_letters.length > 0) {
        handleSelectCompany(data.warning_letters[0]);
      }
      
    } catch (error) {
      console.error("Failed to fetch warning letters:", error);
      setErrorMessage(`Failed to fetch warning letters: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoadingWarningLetters(false);
    }
  };

  // Fetch detailed information for a specific company
  const handleSelectCompany = async (warningLetter) => {
    try {
      setIsLoadingCompanyDetails(true);
      setErrorMessage(null);
      
      // Fetch all warning letters for this company
      const params = new URLSearchParams({
        page: '1',
        page_size: '100',
        company_name: warningLetter.company_name
      });
      
      const response = await ieqipCoreServiceApi.get(
        `/api/warning-letter/extractions?${params.toString()}`, 
        requestHeader
      );
      
      const data = response.data;
      
      // Build company data structure
      const companyData = {
        name: warningLetter.company_name,
        address: warningLetter.company_street_address,
        city: warningLetter.company_city,
        state: warningLetter.company_state,
        zipCode: warningLetter.company_zip_code,
        country: warningLetter.company_country,
        feiNumber: warningLetter.fei_number,
        warningLetters: data.warning_letters || []
      };
      
      setSelectedCompanyData(companyData);
      setOpenCompanyPicker(false);
      
    } catch (error) {
      console.error("Failed to fetch company details:", error);
      setErrorMessage(`Failed to fetch company details: ${error.message}`);
    } finally {
      setIsLoadingCompanyDetails(false);
    }
  };

  const handleViewDetails = (warningLetter) => {
    setSelectedWarningLetter(warningLetter);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedWarningLetter(null);
  };

  const handleOpenCompanyPicker = () => {
    setOpenCompanyPicker(true);
  };

  const handleCloseCompanyPicker = () => {
    setOpenCompanyPicker(false);
  };

  const handleWarningLetterPageChange = (event, value) => {
    setWarningLetterPage(value);
  };

  const handleSearchChange = (event) => {
    setCompanySearchTerm(event.target.value);
    setWarningLetterPage(1); // Reset to first page on search
  };

  const getExtractionStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getLetterStatusColor = (status) => {
    switch (status) {
      case "open":
        return "error";
      case "closed":
        return "success";
      case "responded":
        return "info";
      default:
        return "default";
    }
  };

  // Calculate company statistics
  const getCompanyStats = () => {
    if (!selectedCompanyData) return { totalWarningLetters: 0, totalViolations: 0, totalProducts: 0 };

    const warningLetters = selectedCompanyData.warningLetters || [];
    return {
      totalWarningLetters: warningLetters.length,
      totalViolations: warningLetters.reduce((sum, wl) => sum + (wl.violation_count || 0), 0),
      totalProducts: warningLetters.reduce((sum, wl) => sum + (wl.product_count || 0), 0),
    };
  };

  // Get date range details
  const getDateDetails = () => {
    if (!selectedCompanyData) return { earliest: "N/A", latest: "N/A" };

    const warningLetters = selectedCompanyData.warningLetters || [];
    const dates = warningLetters.map(wl => wl.date_issued).filter(d => d).sort();
    
    if (dates.length === 0) return { earliest: "N/A", latest: "N/A" };

    return {
      earliest: dates[0],
      latest: dates[dates.length - 1]
    };
  };

  // Get unique FDA centers
  const getFdaCenters = () => {
    if (!selectedCompanyData) return [];

    const warningLetters = selectedCompanyData.warningLetters || [];
    const centers = [...new Set(warningLetters.map(wl => wl.fda_center).filter(c => c))];
    return centers;
  };

  // Get all violations across warning letters
  const getAllViolations = () => {
    if (!selectedCompanyData) return [];

    const warningLetters = selectedCompanyData.warningLetters || [];
    const violations = [];
    
    warningLetters.forEach(wl => {
      if (wl.violations && wl.violations.length > 0) {
        wl.violations.forEach(v => {
          violations.push({
            ...v,
            warningLetterNumber: wl.warning_letter_number,
            dateIssued: wl.date_issued
          });
        });
      }
    });

    return violations;
  };

  const stats = getCompanyStats();
  const dateDetails = getDateDetails();
  const fdaCenters = getFdaCenters();
  const allViolations = getAllViolations();

  return (
    <Box m="20px">
      {/* Page Title */}
      <Typography variant="h2" fontWeight="bold" color="#1976d2" mb={2}>
        Warning Letter Analysis
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="warning letter tabs">
          <Tab 
            icon={<BusinessIcon />} 
            iconPosition="start" 
            label="Company Report" 
            id="tab-0"
            aria-controls="tabpanel-0"
          />
          <Tab 
            icon={<DescriptionIcon />} 
            iconPosition="start" 
            label="Warning Letter Extractions" 
            id="tab-1"
            aria-controls="tabpanel-1"
          />
        </Tabs>
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Tab 0: Company Report */}
      <Box role="tabpanel" hidden={activeTab !== 0} id="tabpanel-0" aria-labelledby="tab-0">
        {activeTab === 0 && (
          <>
            {/* Header with Company Selection */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box display="flex" alignItems="center" gap={3}>
                <Typography variant="h3" fontWeight="bold" color="#1976d2">
                  Company Warning Letter Report
                </Typography>
              </Box>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<BusinessIcon />}
                  onClick={handleOpenCompanyPicker}
                  sx={{ minWidth: 200 }}
                >
                  {selectedCompanyData 
                    ? `${selectedCompanyData.name}` 
                    : "Select Company"}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchWarningLetters()}
                  disabled={isLoadingWarningLetters}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {isLoadingCompanyDetails && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
              </Box>
            )}

            {selectedCompanyData && !isLoadingCompanyDetails && (
        <>
          {/* Company Header Information */}
          <Typography variant="h3" fontWeight="bold" color="#1976d2" mb={3} textAlign="center">
            {selectedCompanyData.name} - Warning Letter Summary
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Company Address */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LocationOnIcon color="primary" />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Company Address:
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  {selectedCompanyData.address || "N/A"}
                </Typography>
                <Typography variant="body2">
                  {selectedCompanyData.city}, {selectedCompanyData.state} {selectedCompanyData.zipCode}
                </Typography>
                <Typography variant="body2">
                  {selectedCompanyData.country}
                </Typography>
              </Paper>
            </Grid>

            {/* FEI Number */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    FEI Number:
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  {selectedCompanyData.feiNumber || "N/A"}
                </Typography>
              </Paper>
            </Grid>

            {/* FDA Centers */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <GavelIcon color="primary" />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    FDA Centers:
                  </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {fdaCenters.length > 0 ? fdaCenters.map((center, idx) => (
                    <Chip key={idx} label={center} size="small" color="primary" variant="outlined" />
                  )) : (
                    <Typography variant="body1">N/A</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Overview Statistics */}
          <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
            Overview Statistics
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #5472d3 0%, #3f51b5 100%)', 
                color: 'white',
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h1" fontWeight="bold" mb={1}>
                    {stats.totalWarningLetters}
                  </Typography>
                  <Typography variant="h6" fontWeight="500">
                    Total Warning Letters
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #5472d3 0%, #3f51b5 100%)', 
                color: 'white',
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h1" fontWeight="bold" mb={1}>
                    {stats.totalViolations}
                  </Typography>
                  <Typography variant="h6" fontWeight="500">
                    Total Violations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #5472d3 0%, #3f51b5 100%)', 
                color: 'white',
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h1" fontWeight="bold" mb={1}>
                    {stats.totalProducts}
                  </Typography>
                  <Typography variant="h6" fontWeight="500">
                    Products Cited
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Date Details */}
          <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
            Timeline
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CalendarTodayIcon color="primary" />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Earliest Warning Letter:
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  {dateDetails.earliest}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CalendarTodayIcon color="primary" />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Latest Warning Letter:
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  {dateDetails.latest}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Warning Letters Table */}
          <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
            {selectedCompanyData.name}'s Warning Letters
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#1976d2' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Letter #</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date Issued</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>FDA Center</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Subject</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Violations</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedCompanyData.warningLetters
                  .sort((a, b) => new Date(b.date_issued) - new Date(a.date_issued))
                  .map((wl, index) => (
                  <TableRow key={index}>
                    <TableCell>{wl.warning_letter_number || "N/A"}</TableCell>
                    <TableCell>{wl.date_issued}</TableCell>
                    <TableCell>{wl.fda_center || "N/A"}</TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" noWrap title={wl.subject_line}>
                        {wl.subject_line || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={wl.violation_count || 0} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={wl.letter_status?.toUpperCase() || "N/A"} 
                        size="small" 
                        color={getLetterStatusColor(wl.letter_status)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleViewDetails(wl)}
                        size="small"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Violations Summary Section */}
          {allViolations.length > 0 && (
            <>
              <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
                All Violations Summary
              </Typography>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#1976d2' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Letter #</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Regulation Title</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Citation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allViolations.slice(0, 20).map((violation, index) => (
                      <TableRow key={index}>
                        <TableCell>{violation.warningLetterNumber}</TableCell>
                        <TableCell>{violation.dateIssued}</TableCell>
                        <TableCell>{violation.regulation_title || "N/A"}</TableCell>
                        <TableCell>
                          <Chip 
                            label={violation.regulation_citation || "N/A"} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
            </>
          )}
          </>
        )}
      </Box>

      {/* Tab 1: Warning Letter Extractions List */}
      <Box role="tabpanel" hidden={activeTab !== 1} id="tabpanel-1" aria-labelledby="tab-1">
        {activeTab === 1 && (
          <>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h3" fontWeight="bold" color="#1976d2">
                Warning Letter Extractions
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshFilteredList}
                disabled={isLoadingFilteredList}
              >
                Refresh
              </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FilterListIcon color="primary" />
                <Typography variant="h6" color="primary" fontWeight="bold">
                  Filters:
                </Typography>
                
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={countryFilter}
                    label="Country"
                    onChange={handleCountryFilterChange}
                  >
                    <MenuItem value="">All Countries</MenuItem>
                    {availableCountries.map((country) => (
                      <MenuItem key={country} value={country}>{country}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={yearFilter}
                    label="Year"
                    onChange={handleYearFilterChange}
                  >
                    <MenuItem value="">All Years</MenuItem>
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(countryFilter || yearFilter) && (
                  <Button 
                    variant="text" 
                    color="secondary" 
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>
                )}

                <Box sx={{ flexGrow: 1 }} />
                
                <Typography variant="body2" color="textSecondary">
                  {filteredTotalCount} total extractions
                </Typography>
              </Box>
            </Paper>

            {/* Loading State */}
            {isLoadingFilteredList && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
              </Box>
            )}

            {/* Warning Letters Table */}
            {!isLoadingFilteredList && (
              <>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: '#1976d2' }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Company Name</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Letter #</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Country</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date Issued</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Violations</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredWarningLetters.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body1" color="textSecondary" py={4}>
                              No warning letters found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredWarningLetters.map((wl, index) => (
                          <TableRow key={wl.id || index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {wl.company_name || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>{wl.warning_letter_number || "N/A"}</TableCell>
                            <TableCell>{wl.company_country || "N/A"}</TableCell>
                            <TableCell>{wl.date_issued || "N/A"}</TableCell>
                            <TableCell>
                              <Chip 
                                label={wl.violation_count || 0} 
                                size="small" 
                                color="primary" 
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={wl.extraction_status?.toUpperCase() || "N/A"} 
                                size="small"
                                color={getExtractionStatusColor(wl.extraction_status)}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleViewDetails(wl)}
                                size="small"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {filteredTotalPages > 1 && (
                  <Box display="flex" justifyContent="center" mb={3}>
                    <Pagination
                      count={filteredTotalPages}
                      page={filteredPage}
                      onChange={handleFilteredPageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {/* Company Picker Dialog */}
      <Dialog
        open={openCompanyPicker}
        onClose={handleCloseCompanyPicker}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">Select a Company</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search company by name..."
            value={companySearchTerm}
            onChange={handleSearchChange}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Result Count */}
          {!isLoadingWarningLetters && warningLetterTotalCount > 0 && (
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Showing {warningLetters.length} of {warningLetterTotalCount} warning letters 
                {warningLetterTotalPages > 1 && ` (Page ${warningLetterPage} of ${warningLetterTotalPages})`}
              </Typography>
            </Box>
          )}

          {/* Loading State */}
          {isLoadingWarningLetters && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          )}

          {/* Warning Letters List */}
          {!isLoadingWarningLetters && warningLetters.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No warning letters found
              </Typography>
            </Box>
          )}

          {!isLoadingWarningLetters && warningLetters.length > 0 && (
            <Box>
              <List>
                {warningLetters.map((wl, index) => (
                  <React.Fragment key={wl.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        },
                        backgroundColor: 
                          selectedCompanyData?.name === wl.company_name 
                            ? 'action.selected' 
                            : 'transparent'
                      }}
                      onClick={() => handleSelectCompany(wl)}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            {wl.company_name}
                          </Typography>
                          <Chip 
                            label={wl.extraction_status || "N/A"} 
                            size="small"
                            color={getExtractionStatusColor(wl.extraction_status)}
                          />
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                              Letter #: {wl.warning_letter_number || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                              Date Issued: {wl.date_issued || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                              FDA Center: {wl.fda_center || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                              Violations: {wl.violation_count || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </ListItem>
                    {index < warningLetters.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              {/* Pagination */}
              {warningLetterTotalPages > 1 && (
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  gap={2}
                  mt={3} 
                  pt={2} 
                  borderTop={1} 
                  borderColor="divider"
                >
                  <Typography variant="body2" color="textSecondary">
                    Page {warningLetterPage} of {warningLetterTotalPages}
                  </Typography>
                  <Pagination
                    count={warningLetterTotalPages}
                    page={warningLetterPage}
                    onChange={handleWarningLetterPageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {warningLetterTotalCount > 0 && `Total: ${warningLetterTotalCount} warning letters`}
          </Typography>
          <Button onClick={handleCloseCompanyPicker} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Letter Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <WarningAmberIcon color="primary" />
              <Typography variant="h5">Warning Letter Details</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Chip
                label={selectedWarningLetter?.letter_status?.toUpperCase() || "UNKNOWN"}
                color={getLetterStatusColor(selectedWarningLetter?.letter_status)}
              />
              <Chip
                label={selectedWarningLetter?.extraction_status?.toUpperCase() || "UNKNOWN"}
                color={getExtractionStatusColor(selectedWarningLetter?.extraction_status)}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedWarningLetter && (
            <Box>
              {/* Letter Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom color="primary">Letter Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Warning Letter Number</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedWarningLetter.warning_letter_number || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Letter Type</Typography>
                    <Typography variant="body1">{selectedWarningLetter.letter_type || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">FDA Center</Typography>
                    <Typography variant="body1">{selectedWarningLetter.fda_center || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Subject</Typography>
                    <Typography variant="body1">{selectedWarningLetter.subject_line || "N/A"}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Company Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom color="primary">Company Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Company Name</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedWarningLetter.company_name || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">FEI Number</Typography>
                    <Typography variant="body1">{selectedWarningLetter.fei_number || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Address</Typography>
                    <Typography variant="body1">
                      {selectedWarningLetter.company_street_address || "N/A"}
                      {selectedWarningLetter.company_city ? `, ${selectedWarningLetter.company_city}` : ""}
                      {selectedWarningLetter.company_state ? `, ${selectedWarningLetter.company_state}` : ""}
                      {selectedWarningLetter.company_zip_code ? ` ${selectedWarningLetter.company_zip_code}` : ""}
                    </Typography>
                    <Typography variant="body1">{selectedWarningLetter.company_country || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Recipient</Typography>
                    <Typography variant="body1">
                      {selectedWarningLetter.recipient_name || "N/A"}
                      {selectedWarningLetter.recipient_title ? ` (${selectedWarningLetter.recipient_title})` : ""}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Dates Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom color="primary">Important Dates</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Date Issued</Typography>
                    <Typography variant="body1">{selectedWarningLetter.date_issued || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Response Due Date</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {selectedWarningLetter.response_due_date || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Close Out Date</Typography>
                    <Typography variant="body1">{selectedWarningLetter.close_out_date || "N/A"}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Sender Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom color="primary">Issuing Office</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Sender</Typography>
                    <Typography variant="body1">
                      {selectedWarningLetter.sender_name || "N/A"}
                      {selectedWarningLetter.sender_title ? `, ${selectedWarningLetter.sender_title}` : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Division</Typography>
                    <Typography variant="body1">{selectedWarningLetter.sender_division || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Issuing Office</Typography>
                    <Typography variant="body1">{selectedWarningLetter.issuing_office || "N/A"}</Typography>
                  </Grid>
                  {selectedWarningLetter.issuing_office_address && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Issuing Office Address</Typography>
                      <Typography variant="body1">{selectedWarningLetter.issuing_office_address}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Violations */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Violations ({selectedWarningLetter.violations?.length || 0})
                </Typography>
                {selectedWarningLetter.violations && selectedWarningLetter.violations.length > 0 ? (
                  <List>
                    {selectedWarningLetter.violations.map((violation, index) => (
                      <React.Fragment key={index}>
                        <ListItem alignItems="flex-start" sx={{ flexDirection: "column", alignItems: "flex-start", py: 2 }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <Chip
                              label={`#${violation.violation_number}`}
                              size="small"
                              color="primary"
                              sx={{ mr: 1 }}
                            />
                            <Typography variant="subtitle2" fontWeight="bold">
                              {violation.regulation_title || "Untitled Violation"}
                            </Typography>
                          </Box>
                          <Chip 
                            label={violation.regulation_citation || "No Citation"} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                            sx={{ mb: 1 }}
                          />
                          <Box
                            sx={{
                              mt: 1,
                              color: "text.secondary",
                              "& pre": MESSAGE_STYLES.pre,
                              "& code": MESSAGE_STYLES.code,
                              "& strong": MESSAGE_STYLES.strong,
                              "& em": MESSAGE_STYLES.em,
                              "& h1": MESSAGE_STYLES.h1,
                              "& h2": MESSAGE_STYLES.h2,
                              "& h3": MESSAGE_STYLES.h3,
                              "& h4": MESSAGE_STYLES.h4,
                              "& h5": MESSAGE_STYLES.h5,
                              "& h6": MESSAGE_STYLES.h6,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word"
                            }}
                            dangerouslySetInnerHTML={{
                              __html: convertMessageToHtml(violation.violation_description || "No description available")
                            }}
                          />
                        </ListItem>
                        {index < selectedWarningLetter.violations.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No violations found
                  </Typography>
                )}
              </Paper>

              {/* Products Cited */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Products Cited ({selectedWarningLetter.products_cited?.length || 0})
                </Typography>
                {selectedWarningLetter.products_cited && selectedWarningLetter.products_cited.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Product Name</strong></TableCell>
                          <TableCell><strong>Product Type</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedWarningLetter.products_cited.map((product, index) => (
                          <TableRow key={index}>
                            <TableCell>{product.product_name || "N/A"}</TableCell>
                            <TableCell>{product.product_type || "N/A"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No products cited
                  </Typography>
                )}
              </Paper>

              {/* Citations */}
              <Paper elevation={0} sx={{ p: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Citations ({selectedWarningLetter.citations?.length || 0})
                </Typography>
                {selectedWarningLetter.citations && selectedWarningLetter.citations.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedWarningLetter.citations.map((citation, index) => (
                      <Chip 
                        key={index}
                        label={citation.citation_code || "N/A"} 
                        color="primary"
                        variant="outlined"
                        title={citation.citation_description}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No citations found
                  </Typography>
                )}
              </Paper>

              {/* Error Message if any */}
              {selectedWarningLetter.error_message && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Error:</strong> {selectedWarningLetter.error_message}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarningLetterSummary;
